import { useState, useCallback, useRef } from "react";
import { useAppSelector } from "@/store/hooks";
import { debounce } from "@/utils/debounce";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { listProductInfos, listBrands, listCategories } from "@/graphql/queries";
import { getDefaultParseVariants } from "./useCustomPaginatedProducts";
import { sortProductByName } from "@/utils/helper/sortProductByName";

export type SearchMode = "products" | "brands" | "categories";

export type SearchResult = {
  id: string;
  name: string;
  image?: string;
  price?: number;
  brand_id?: string;
  type: SearchMode;
};

interface UseEnhancedSearchOptions {
  debounceMs?: number;
}

export function useEnhancedSearch({ debounceMs = 400 }: UseEnhancedSearchOptions = {}) {
  const { brandsMap } = useAppSelector((state) => state.app);
  const [input, setInput] = useState("");
  const [searchMode, setSearchMode] = useState<SearchMode>("products");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearchRef = useRef(
    debounce(async (searchValue: string, mode: SearchMode) => {
      setError(null);
      setResults([]);
      setLoading(true);

      try {
        let searchResults: SearchResult[] = [];

        switch (mode) {
          case "products":
            const productRes = await executeGraphQLMutation({
              query: listProductInfos,
              variables: {
                filter: {
                  name: { contains: searchValue?.slice(0, 1)?.toUpperCase() + searchValue?.slice(1) },
                  is_active: { eq: true },
                  is_deleted: { eq: false },
                },
              },
              showToast: false,
            });
            
            if (productRes?.items) {
              const variants = getDefaultParseVariants(productRes.items);
              searchResults = sortProductByName(variants).map((product: any) => ({
                id: product.product_id || product.id,
                name: product.name,
                image: product.image,
                price: product.price,
                brand_id: product.brand_id,
                type: "products" as SearchMode,
              }));
            }
            break;

          case "brands":
            const brandRes = await executeGraphQLMutation({
              query: listBrands,
              variables: {
                filter: {
                  name: { contains: searchValue?.toLowerCase() },
                  is_active: { eq: true },
                  is_deleted: { eq: false },
                },
              },
              showToast: false,
            });
            
            if (brandRes?.items) {
              searchResults = brandRes.items
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((brand: any) => ({
                  id: brand.id,
                  name: brand.name,
                  image: brand.image,
                  type: "brands" as SearchMode,
                }));
            }
            break;

          case "categories":
            const categoryRes = await executeGraphQLMutation({
              query: listCategories,
              variables: {
                filter: {
                  name: { contains: searchValue?.toLowerCase() },
                  is_active: { eq: true },
                  is_deleted: { eq: false },
                },
              },
              showToast: false,
            });
            
            if (categoryRes?.items) {
              searchResults = categoryRes.items
                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                .map((category: any) => ({
                  id: category.id,
                  name: category.name,
                  image: category.image,
                  type: "categories" as SearchMode,
                }));
            }
            break;
        }

        setResults(searchResults);
      } catch (err: any) {
        setError(err.message || "Error searching");
      } finally {
        setLoading(false);
      }
    }, debounceMs)
  );

  const onInputChange = useCallback(
    (val: string) => {
      setInput(val);
      if (val.trim()) {
        debouncedSearchRef.current(val.trim(), searchMode);
      } else {
        setResults([]);
      }
    },
    [searchMode]
  );

  const onSearchModeChange = useCallback((mode: SearchMode) => {
    setSearchMode(mode);
    setResults([]);
    if (input.trim()) {
      debouncedSearchRef.current(input.trim(), mode);
    }
  }, [input]);

  const getNavigationLink = useCallback((result: SearchResult) => {
    switch (result.type) {
      case "products":
        return `/product/${result.id}`;
      case "brands":
        return `/brand/${result.id}`;
      case "categories":
        return `/category/${result.id}`;
      default:
        return "/";
    }
  }, []);

  return {
    input,
    setInput: onInputChange,
    searchMode,
    setSearchMode: onSearchModeChange,
    results,
    loading,
    error,
    getNavigationLink,
    brandsMap,
  };
} 